// Libraries
import * as am5 from '@amcharts/amcharts5';
import { TimeUnit } from '@amcharts/amcharts5/.internal/core/util/Time';

// Types
import { MeasurementType } from '../../../types/global';

export enum SeriesColorEnum {
  purple = 'purple',
  yellow = 'yellow',
  lightOrange = 'lightOrange',
  green = 'green',
  orange = 'orange',
  blue = 'blue',
  pink = 'pink',
  lightGreen = 'lightGreen',
  lightPink = 'lightPink',
  lightBlue = 'lightBlue',
}

export type SeriesColor = string | SeriesColorEnum;

export enum LineChartType {
  fill = 'fill',
  line = 'line',
  status = 'status',
}

export type Series = {
  bullet?: boolean;
  color?: SeriesColor;
  date: string;
  decimals?: number;
  field: string;
  name: string;
  opposite?: boolean;
  status?: string[];
  type: LineChartType;
  unit?: string;
  measurementType?: MeasurementType;
  minScale?: number;
  maxScale?: number;
};

export interface LineChartProps {
  activeBullet?: boolean;
  className?: string;
  compactLegend?: boolean;
  data: Record<string, any>[];
  grid?: boolean;
  id: string;
  series: Series[];
  testId?: string;
}

// Data types for chart items
export type ChartDataScalar = number | string | null | undefined;

export interface IChartDataRecord {
  [key: string]: ChartDataScalar;
}

// Chart configuration interfaces
export interface ChartFonts {
  regular: string;
}

export interface ChartColors {
  fillColor: string;
  statusColor: string;
  yAxisColor: string;
  lineSeriesColor: Record<string | SeriesColor, string>;
}

export interface ChartConfiguration {
  fonts: ChartFonts;
  colors: ChartColors;
}

// Base interval calculation result
export interface BaseIntervalResult {
  timeUnit: TimeUnit;
  count: number;
}

// Adapter context interfaces
export interface DataItemAdapter {
  get: (key: string) => number | undefined;
}

export interface AdapterTarget {
  dataItem?: DataItemAdapter;
}

// Gap analysis result
export interface GapAnalysisResult {
  shouldConnect: boolean;
  hasSignificantGaps: boolean;
  hasExtremeGaps: boolean;
  hasProportionalGaps: boolean;
}

// Status mapping result
export interface StatusMappingResult {
  [key: string]: number;
}

// Interval statistics for base interval calculation
export interface IntervalStatistics {
  sortedIntervals: number[];
  medianInterval: number;
  mostCommonInterval: number;
  mostCommonFrequency: number;
}

// Animation update function type
export type PulseAnimationUpdater = (shouldPulse: boolean) => void;

// Enhanced container with animation update function
export interface AnimatedBulletContainer extends am5.Container {
  updatePulseAnimation?: PulseAnimationUpdater;
}

export type DateFormatKey =
  | 'seconds'
  | 'minutes'
  | 'dateTime'
  | 'dateOnly'
  | 'dateMonth'
  | 'monthYear'
  | 'yearOnly'
  | 'default';

export type DateFormatSet = Record<DateFormatKey, string>;

export enum SupportedLanguage {
  portuguese = 'portuguese',
  english = 'english',
}

export type LanguageFormatSets = Record<SupportedLanguage, DateFormatSet>;
